import { useCallback, useEffect, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, money, STAGES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LabelSheet from "@/components/LabelSheet";
import LabelPrintDialog from "@/components/LabelPrintDialog";
import ImageAnnotator from "@/components/ImageAnnotator";
import SynopticForm from "@/components/SynopticForm";
import { ArrowLeft, Printer, Save, Plus, Send, History, ImagePlus, Eye, Ban, Scissors, Camera, PenLine, X, FileText, FilePen, ClipboardCheck, Receipt, PencilLine, Timer, Users, AlertTriangle } from "lucide-react";
import { EditDialog, RetireDialog } from "@/components/RecordEditor";
import RejectionDialog from "@/components/RejectionDialog";
import RequestDialog from "@/components/RequestDialog";
import DictateButton from "@/components/DictateButton";
import { useCannedText } from "@/components/CannedText";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import { ArchivedPaperPanel } from "@/components/ArchivedPaperPanel";
import { LabLyncPanel, isMarrowCase } from "@/components/LabLyncPanel";

const SENDOUT_STATUSES = ["Requested", "Pending pickup", "In transit", "Received by lab", "In progress", "Results received", "Completed", "Cancelled"];

const SLIDE_TYPES = ["H&E", "Unstained", "Recut", "IHC", "Special Stain", "Frozen", "FNA"];
const SLIDE_CODES = { "H&E": "HE", Unstained: "UNST", Recut: "RC", IHC: "IHC", "Special Stain": "SS", Frozen: "FS", FNA: "FNA" };
const BLOCK_PARTS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
const RECUT_BADGE = {
  Pending: "bg-amber-600 text-white", "In progress": "bg-sky-700 text-white",
  Completed: "bg-green-800 text-white", Cancelled: "bg-slate-300 text-slate-800",
};

const EMPTY_SPEC = {
  kind: "block", part: "A", parent_block_id: "", level: "", slide_type: "H&E",
  quantity: 1, notes: "",
};

const NarrativeField = ({ label, testid, locked, caseId, field, value, onChange, lockNote = "" }) => {
  const canned = useCannedText(field === "comments" ? "comment" : field, value, onChange, { disabled: locked });
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
        {lockNote && (
          <span className="bg-slate-200 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-700"
                data-testid={`${testid}-lock-note`}>{lockNote}</span>
        )}
        {!locked && (
          <DictateButton value={value} onChange={onChange} field={field} caseId={caseId}
                         testid={`dictate-${field}`} />
        )}
        {canned.trigger}
      </div>
      <Textarea ref={canned.ref} value={value} onChange={(e) => onChange(e.target.value)}
                onKeyDown={canned.handleKeyDown} data-testid={testid} disabled={locked}
                className="mt-1 min-h-[150px] border-slate-300 focus-visible:ring-2 focus-visible:ring-[#134e4a]"
                placeholder="Type, dictate, or expand a canned text with .trigger + Tab." />
      {canned.panel}
    </div>
  );
};

export default function CaseDetail() {  const { id } = useParams();
  const { can, user } = useAuth();
  const [data, setData] = useState(null);
  const [narrative, setNarrative] = useState({ gross_description: "", comments: "", diagnosis: "", signout_notes: "" });
  const [statusForm, setStatusForm] = useState({ status: "", note: "" });
  const [correctOpen, setCorrectOpen] = useState(false);
  const [editSpec, setEditSpec] = useState(null);
  const [voidSpec, setVoidSpec] = useState(null);
  const [spec, setSpec] = useState({ ...EMPTY_SPEC });
  const [requests, setRequests] = useState([]);
  const [frozens, setFrozens] = useState([]);
  const [consults, setConsults] = useState([]);
  const [recutOpen, setRecutOpen] = useState(false);
  const [sendout, setSendout] = useState({ destination_lab: "", destination_address: "", reason: "", test_requested: "", cpt_codes: "", materials_sent: "", sendout_status: "Pending pickup" });
  const [showSendout, setShowSendout] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [upload, setUpload] = useState({ file: null, kind: "Gross photo", caption: "" });
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({});
  const [shots, setShots] = useState([]);
  const [annotating, setAnnotating] = useState(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendForm, setAmendForm] = useState({ reason: "", diagnosis: "", comments: "" });
  const [templates, setTemplates] = useState([]);
  const [synTemplateId, setSynTemplateId] = useState("");
  const [synValues, setSynValues] = useState({});
  const [synSaving, setSynSaving] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [labels, setLabels] = useState(null);
  const [printItems, setPrintItems] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cpt, setCpt] = useState({ items: [], note: "" });
  const [cptDraft, setCptDraft] = useState({ code: "", units: 1 });
  const [cptSaving, setCptSaving] = useState(false);
  const [cptSuggestions, setCptSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      const [res, rq] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get("/histology-requests", { params: { case_id: id } }),
      ]);
      setData(res.data);
      setRequests(rq.data.requests || []);
      api.get("/frozen-sections", { params: { case_id: id } })
        .then((r) => setFrozens(r.data)).catch(() => setFrozens([]));
      api.get("/consults", { params: { case_id: id } })
        .then((r) => setConsults(r.data)).catch(() => setConsults([]));
      if (silent) return;
      const c = res.data.case;
      const tpl = await api.get("/templates");
      setTemplates(tpl.data);
      const syn = c.synoptic;
      if (syn) {
        setSynTemplateId(syn.template_id);
        setSynValues(syn.values || {});
      } else {
        const match = tpl.data.find((t) => t.specimen_type && t.specimen_type === c.specimen_type);
        setSynTemplateId(match ? match.id : "");
        setSynValues({});
      }
      setNarrative({
        gross_description: c.gross_description || "", comments: c.comments || "",
        diagnosis: c.diagnosis || "", signout_notes: c.signout_notes || "",
      });
      setStatusForm({ status: c.status, note: "" });
      setCpt({ items: c.cpt_items || [], note: c.cpt_note || "" });
      api.get("/lab").then((r) => setCptSuggestions(r.data.cpt_suggestions || [])).catch(() => {});
    } catch (e) {
      if (!silent) toast.error(apiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(() => load(true), 5000);

  if (!data) return <div className="text-sm text-slate-500" data-testid="case-detail-loading">Loading case…</div>;
  const c = data.case;
  const blocks = data.specimens.filter((s) => s.kind === "block");
  const previewBarcode = (() => {
    const n = Math.max(1, Number(spec.quantity) || 1);
    if (spec.kind === "block") {
      const used = blocks.filter((b) => (b.part || "") === spec.part).length;
      const first = `${c.accession_number}-${spec.part}${used + 1}`;
      return n === 1 ? first : `${first} … ${c.accession_number}-${spec.part}${used + n}`;
    }
    const b = blocks.find((x) => x.id === spec.parent_block_id);
    if (!b) return "select a block…";
    const designation = b.block_designation || b.label;
    const code = SLIDE_CODES[spec.slide_type] || "SL";
    const start = Number(spec.level) || (data.specimens.filter((s) => s.kind === "slide" && s.parent_block_id === spec.parent_block_id && s.slide_type === spec.slide_type).length + 1);
    const first = `${c.accession_number}-${designation}-L${start}-${code}`;
    return n === 1 ? first : `${first} … ${c.accession_number}-${designation}-L${start + n - 1}-${code}`;
  })();
  const canRequestRecut = ["pathologist", "admin", "lab_director"].includes(user?.role);
  const canCode = ["admin", "lab_director", "supervisor", "pathologist", "pathologist_assistant", "tech"].includes(user?.role);

  const addCpt = (e) => {
    e.preventDefault();
    const code = (cptDraft.code || "").split(" — ")[0].trim();
    if (!code) return toast.error("Enter a CPT code");
    if (cpt.items.some((i) => i.code === code)) return toast.error("That code is already on the case — bump its units instead");
    const match = cptSuggestions.find((s) => s.startsWith(code));
    setCpt((p) => ({
      ...p,
      items: [...p.items, { code, description: match ? match.split(" — ")[1] : "", units: Math.max(1, Number(cptDraft.units) || 1) }],
    }));
    setCptDraft({ code: "", units: 1 });
  };

  const saveCpt = async () => {
    setCptSaving(true);
    try {
      await api.put(`/cases/${id}/cpt`, { items: cpt.items, note: cpt.note });
      toast.success("Billing codes saved");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setCptSaving(false); }
  };

  const isFinalized = ["Final", "Amended"].includes(c.report_status);
  const activeTemplate = templates.find((t) => t.id === synTemplateId) || null;
  const synMissing = (activeTemplate?.fields || [])
    .filter((f) => f.required && (synValues[f.key] === undefined || synValues[f.key] === "" || (Array.isArray(synValues[f.key]) && synValues[f.key].length === 0)))
    .map((f) => f.label);

  const saveNarrative = async () => {
    setBusy(true);
    try {
      await api.put(`/cases/${id}`, narrative);
      toast.success("Case narrative saved");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const changeStatus = async () => {
    try {
      await api.post(`/cases/${id}/status`, statusForm);
      toast.success(`Status set to ${statusForm.status}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addSpecimen = async (e) => {
    e.preventDefault();
    try {
      if (spec.kind === "slide" && !spec.parent_block_id) {
        toast.error("Select the block this slide is cut from");
        return;
      }
      const { data: created } = await api.post("/specimens", {
        ...spec, case_id: id,
        quantity: Number(spec.quantity) || 1,
        level: Number(spec.level) || 0,
      });
      toast.success(created.created_count > 1
        ? `Logged ${created.created_count} ${spec.kind}s: ${created.created_barcodes.join(", ")}`
        : `Logged ${created.barcode}`);
      setSpec((s) => ({ ...EMPTY_SPEC, kind: s.kind, part: s.part, parent_block_id: s.parent_block_id, slide_type: s.slide_type }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const setRecutStatus = async (row, status) => {
    try {
      const { data: updated } = await api.post(`/recuts/${row.id}/status`, { status });
      toast.success(status === "Completed"
        ? `Cut ${(updated.created_barcodes || []).join(", ")}`
        : `Request marked ${status}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const createSendout = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sendouts", { ...sendout, case_id: id });
      toast.success("Case flagged as sent out");
      setSendout({ destination_lab: "", destination_address: "", reason: "", test_requested: "", cpt_codes: "", materials_sent: "", sendout_status: "Pending pickup" });
      setShowSendout(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const updateSendoutStatus = async (row, status) => {
    try {
      await api.put(`/sendouts/${row.id}`, { sendout_status: status });
      toast.success(`Send-out marked ${status}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadPreview = async (att) => {
    if (previews[att.id]) return;
    try {
      const url = att.has_annotations ? `/attachments/${att.id}/annotated` : `/attachments/${att.id}/download`;
      const res = await api.get(url, { responseType: "blob" });
      setPreviews((p) => ({ ...p, [att.id]: URL.createObjectURL(res.data) }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadImage = async (e) => {
    e.preventDefault();
    if (!upload.file) { toast.error("Choose an image first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", upload.file);
    try {
      await api.post(`/cases/${id}/attachments`, fd, {
        params: { kind: upload.kind, caption: upload.caption },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image attached");
      setUpload({ file: null, kind: upload.kind, caption: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const addShots = (files) => {
    setShots((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({ file, kind: "Gross photo", caption: "", url: URL.createObjectURL(file) })),
    ]);
  };

  const uploadShots = async () => {
    if (!shots.length) return;
    setUploading(true);
    let ok = 0;
    for (const shot of shots) {
      const fd = new FormData();
      fd.append("file", shot.file, shot.file.name || `photo-${Date.now()}.jpg`);
      try {
        await api.post(`/cases/${id}/attachments`, fd, {
          params: { kind: shot.kind, caption: shot.caption },
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (err) { toast.error(apiError(err)); }
    }
    setUploading(false);
    if (ok) {
      toast.success(`Attached ${ok} photo(s)`);
      setShots([]);
      load();
    }
  };

  const toggleReportImage = async (att) => {
    try {
      await api.put(`/attachments/${att.id}/report-flag`, { include: !att.include_in_report });
      toast.success(att.include_in_report ? "Removed from the report" : "Will print on the report");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveAnnotations = async (shapes, flattened) => {
    try {
      await api.put(`/attachments/${annotating.att.id}/annotations`, { shapes, flattened_png: flattened });
      toast.success("Marks saved");
      setAnnotating(null);
      setPreviews((p) => { const n = { ...p }; delete n[annotating.att.id]; return n; });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAnnotator = async (att) => {
    try {
      const [blob, meta] = await Promise.all([
        api.get(`/attachments/${att.id}/download`, { responseType: "blob" }),
        api.get(`/attachments/${att.id}/annotations`),
      ]);
      setAnnotating({ att, src: URL.createObjectURL(blob.data), shapes: meta.data.shapes || [] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeImage = async (att) => {
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Image removed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const voidCase = async () => {    if (voidReason.trim().length < 5) { toast.error("Enter a reason of at least 5 characters"); return; }
    try {
      await api.post(`/cases/${id}/void`, { reason: voidReason.trim() });
      toast.success(`${c.accession_number} voided`);
      setVoidOpen(false);
      setVoidReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSynoptic = async () => {
    setSynSaving(true);
    try {
      const { data: res } = await api.put(`/cases/${id}/synoptic`, {
        template_id: synTemplateId, values: synValues,
      });
      if (res.missing_required?.length) {
        toast.warning(`Saved — still needed: ${res.missing_required.join(", ")}`);
      } else {
        toast.success(synTemplateId ? "Synoptic report saved" : "Synoptic report cleared");
      }
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSynSaving(false); }
  };

  const finalizeReport = async () => {
    if (!narrative.diagnosis.trim()) { toast.error("Enter a diagnosis before finalizing the report"); return; }
    setBusy(true);
    try {
      await api.post(`/cases/${id}/signout`, {
        diagnosis: narrative.diagnosis, comments: narrative.comments, signout_notes: narrative.signout_notes,
      });
      toast.success("Report finalized and signed");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submitAmend = async (e) => {
    e.preventDefault();
    if (amendForm.reason.trim().length < 5) { toast.error("Enter a reason of at least 5 characters"); return; }
    try {
      await api.post(`/cases/${id}/amend`, {
        reason: amendForm.reason.trim(), diagnosis: amendForm.diagnosis, comments: amendForm.comments,
      });
      toast.success("Amended report filed");
      setAmendForm({ reason: "", diagnosis: "", comments: "" });
      setAmendOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const restoreCase = async () => {
    try {
      await api.post(`/cases/${id}/restore`);
      toast.success("Case reinstated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleCheckout = async (s) => {
    try {
      const path = s.checked_out ? "return" : "checkout";
      await api.post(`/specimens/${s.id}/${path}`, { to_whom: user?.name || "", note: "" });
      toast.success(s.checked_out ? "Returned to inventory" : "Checked out");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="case-detail-page">
      {labels && <LabelSheet items={labels} onClose={() => setLabels(null)} />}
      <LabelPrintDialog open={!!printItems} onOpenChange={(o) => !o && setPrintItems(null)}
                        items={printItems || []} caseId={id}
                        caseIds={printItems && printItems.length === 0 ? [id] : []}
                        defaultSpec={printItems && printItems.length === 0 ? "container" : "slide"} />
      <RejectionDialog open={rejectOpen} onOpenChange={setRejectOpen} caseId={id}
                       accession={c.accession_number} onLogged={load} />

      <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#134e4a]" data-testid="back-to-cases">
        <ArrowLeft size={14} /> All cases
      </Link>

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Void {c.accession_number} — ordered in error</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              The case is removed from all worklists, dashboards and statistics, and its blocks, slides and send-outs
              are excluded. Nothing is erased — the record, its history and this reason stay in the audit log.
            </p>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Reason *</Label>
              <Textarea
                value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                data-testid="void-reason-input" className="mt-1 min-h-[90px] border-slate-300"
                placeholder="e.g. Duplicate order — specimen accessioned twice by the front desk"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setVoidOpen(false)} data-testid="void-cancel-button" className="h-11">Cancel</Button>
              <Button onClick={voidCase} data-testid="void-confirm-button" className="h-11 bg-red-800 hover:bg-red-900">
                Void case
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={amendOpen} onOpenChange={setAmendOpen}>
        <DialogContent className="max-w-lg" data-testid="amend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Amend report — {c.accession_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAmend} className="space-y-3" data-testid="amend-form">
            <p className="text-sm text-slate-600">
              The signed report stays on file. This files an amended version (v{Number(c.report_version || 1) + 1}) with your
              reason, and keeps the previous diagnosis in the report's amendment history.
            </p>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Reason for amendment *</Label>
              <Textarea value={amendForm.reason} onChange={(e) => setAmendForm((f) => ({ ...f, reason: e.target.value }))}
                data-testid="amend-reason-input" className="mt-1 min-h-[80px] border-slate-300"
                placeholder="e.g. IHC results received after sign-out — diagnosis revised" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Revised diagnosis (leave blank to keep)</Label>
              <Textarea value={amendForm.diagnosis} onChange={(e) => setAmendForm((f) => ({ ...f, diagnosis: e.target.value }))}
                data-testid="amend-diagnosis-input" className="mt-1 min-h-[100px] border-slate-300" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Revised comment (optional)</Label>
              <Textarea value={amendForm.comments} onChange={(e) => setAmendForm((f) => ({ ...f, comments: e.target.value }))}
                data-testid="amend-comments-input" className="mt-1 min-h-[70px] border-slate-300" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAmendOpen(false)} data-testid="amend-cancel-button" className="h-11">Cancel</Button>
              <Button type="submit" data-testid="amend-confirm-button" className="h-11 bg-amber-700 hover:bg-amber-800">File amendment</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {data.rejection_hold && (
        <div className="flex flex-wrap items-center gap-3 border-l-2 border-amber-600 bg-amber-50 p-4"
             data-testid="case-rejection-hold-banner">
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Held — specimen discrepancy on receipt.</span>{" "}
            {data.rejection_hold.rejection_number} · {data.rejection_hold.discrepancy_type} —
            {" "}{data.rejection_hold.description} · found by {data.rejection_hold.discovered_by}.
            {" "}This case cannot advance until the discrepancy is resolved.
          </div>
          <Link to="/accreditation/rejections" data-testid="case-rejection-hold-link"
                className="ml-auto text-sm font-semibold text-amber-900 underline">
            Resolve it
          </Link>
        </div>
      )}

      {c.voided && (
        <div className="flex flex-wrap items-center gap-3 border-l-2 border-red-800 bg-red-50 p-4" data-testid="case-voided-banner">          <div className="text-sm text-red-900">
            <span className="font-semibold">Voided — ordered in error.</span>{" "}
            {c.void_reason} · voided by {c.voided_by} ({c.voided_by_role}) on {fmtDateTime(c.voided_at)}
          </div>
          {can("cases:delete") && (
            <Button size="sm" variant="outline" onClick={restoreCase} data-testid="restore-case-button" className="ml-auto h-9">
              Reinstate case
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-mono text-3xl font-bold tracking-tight text-[#134e4a]" data-testid="case-accession-number">{c.accession_number}</h1>
            {c.priority === "STAT" && <span className="bg-red-800 px-2 py-1 text-xs font-bold text-white">STAT</span>}
            {(c.sent_out || data.sendouts.length > 0) && (
              <span className="bg-sky-800 px-2 py-1 text-xs font-bold text-white" data-testid="case-sent-out-badge">SENT OUT</span>
            )}
            {c.voided && <span className="bg-red-800 px-2 py-1 text-xs font-bold text-white" data-testid="case-voided-badge">VOIDED</span>}
            <span className="border border-[#134e4a]/30 bg-[#134e4a]/5 px-2 py-1 text-xs font-semibold text-[#134e4a]" data-testid="case-status-badge">{c.status}</span>
            {c.report_status === "Final" && (
              <span className="bg-emerald-800 px-2 py-1 text-xs font-bold text-white" data-testid="report-final-badge">FINAL REPORT</span>
            )}
            {c.report_status === "Amended" && (
              <span className="bg-amber-600 px-2 py-1 text-xs font-bold text-white" data-testid="report-amended-badge">AMENDED v{c.report_version}</span>
            )}
            <LiveDot lastSync={lastSync} testid="case-live-indicator" />
          </div>
          <div className="mt-1 text-sm text-slate-700" data-testid="case-patient-name">
            {c.patient_name} · MRN {c.patient_mrn || "—"} · DOB {c.patient_dob || "—"}
            <Link
              to={`/patients?${c.patient_mrn ? `mrn=${encodeURIComponent(c.patient_mrn)}` : `name=${encodeURIComponent(c.patient_name)}`}`}
              data-testid="view-patient-history-link"
              className="ml-2 text-xs font-semibold text-[#134e4a] underline hover:no-underline"
            >
              View full patient history
            </Link>
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            {c.specimen_class || "Surgical"}{c.cytology_type ? ` (${c.cytology_type})` : ""} · {c.specimen_type || "—"} / {c.specimen_site || "—"} · from {c.origin_facility || "—"} · Ordered by {c.ordering_physician || "—"} · Received {fmtDateTime(c.received_at)} · TAT {c.tat_hours}h
          </div>
        </div>
        {can("status:write") && !c.voided && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Advance status</Label>
              <select
                value={statusForm.status} onChange={(e) => setStatusForm((f) => ({ ...f, status: e.target.value }))}
                data-testid="status-select" className="mt-1 h-11 rounded-md border border-slate-300 bg-white px-3 text-sm"
              >
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Input
              value={statusForm.note} onChange={(e) => setStatusForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Note (optional)" data-testid="status-note-input" className="h-11 w-48 border-slate-300"
            />
            <Button onClick={changeStatus} data-testid="status-update-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">Update</Button>
            <Link to={`/cases/${id}/report`} data-testid="view-report-link"
              className="inline-flex h-11 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">
              <FileText size={15} /> Final report
            </Link>
            {["pathologist", "admin", "lab_director"].includes(user?.role) && (
              isFinalized ? (
                <Button variant="outline" onClick={() => setAmendOpen(true)} data-testid="amend-report-button" className="h-11">
                  <FilePen size={15} className="mr-2" /> Amend report
                </Button>
              ) : (
                <Button onClick={finalizeReport} disabled={busy} data-testid="signout-case-button" className="h-11 bg-emerald-800 hover:bg-emerald-900">
                  <FileText size={15} className="mr-2" /> Sign out & finalize
                </Button>
              )
            )}
            {can("cases:void") && (
              <Button variant="outline" onClick={() => setVoidOpen(true)} data-testid="void-case-button"
                className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                <Ban size={15} className="mr-2" /> Void case
              </Button>
            )}
            {can("status:write") && (
              <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="log-rejection-button"
                className="h-11 border-amber-300 text-amber-900 hover:bg-amber-50">
                <AlertTriangle size={15} className="mr-2" /> Log a discrepancy
              </Button>
            )}
            {can("status:write") && (
              <Button variant="outline" onClick={() => setCorrectOpen(true)} data-testid="correct-case-button"
                className="h-11 border-slate-300">
                <PencilLine size={15} className="mr-2" /> Correct case details
              </Button>
            )}
          </div>
        )}
      </div>

      <EditDialog
        open={correctOpen} onOpenChange={setCorrectOpen} testid="correct-case-dialog"
        title={`Correct ${c.accession_number}`}
        description="Fix a wrong accession number or mistyped demographics. Blocks, slides and send-outs are renumbered to match."
        values={c}
        fields={[
          { name: "accession_number", label: "Accession number", hint: "Must stay unique — blocks and slides follow it" },
          { name: "priority", label: "Priority", type: "select", options: ["Routine", "STAT"] },
          { name: "patient_name", label: "Patient name" },
          { name: "patient_mrn", label: "MRN" },
          { name: "patient_dob", label: "Date of birth", type: "date" },
          { name: "ordering_physician", label: "Ordering physician" },
          { name: "specimen_type", label: "Specimen type" },
          { name: "specimen_site", label: "Specimen site" },
          { name: "origin_facility", label: "Origin facility" },
          { name: "specimen_class", label: "Specimen class", type: "select",
            options: ["Surgical", "Cytology", "Hematology", "Autopsy", "Other"] },
        ]}
        onSave={async (body) => {
          await api.put(`/cases/${id}`, body);
          toast.success("Case details corrected");
          load();
        }}
      />

      <EditDialog
        open={!!editSpec} onOpenChange={(v) => !v && setEditSpec(null)} testid="correct-case-specimen-dialog"
        title={`Correct ${editSpec?.barcode || ""}`}
        description="Fix a mistyped barcode, part letter or level. The checkout trail stays intact."
        values={editSpec || {}}
        fields={[
          { name: "barcode", label: "Barcode" },
          { name: "label", label: "Label" },
          { name: "part", label: "Part", type: "select", allowEmpty: true, options: BLOCK_PARTS },
          { name: "level", label: "Level", type: "number" },
          { name: "slide_type", label: "Slide type", type: "select", allowEmpty: true, options: SLIDE_TYPES },
          { name: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        onSave={async (body) => {
          await api.put(`/specimens/${editSpec.id}`, body);
          toast.success("Block/slide corrected");
          load();
        }}
      />

      <RetireDialog
        open={!!voidSpec} onOpenChange={(v) => !v && setVoidSpec(null)} testid="void-case-specimen-dialog"
        title={`Void ${voidSpec?.barcode || ""}`} confirmLabel="Void block/slide"
        description="Use this when a block or slide was logged in error. It leaves inventory but stays on file for the audit trail."
        onConfirm={async (reason) => {
          await api.post(`/specimens/${voidSpec.id}/void`, { reason });
          toast.success("Voided");
          load();
        }}
      />

      <Tabs defaultValue="narrative">
        <div className="overflow-x-auto">
          <TabsList data-testid="case-tabs" className="w-max">
          <TabsTrigger value="narrative" data-testid="tab-narrative">Narrative</TabsTrigger>
          <TabsTrigger value="specimens" data-testid="tab-specimens">Blocks & slides ({data.specimens.length})</TabsTrigger>
          <TabsTrigger value="sendouts" data-testid="tab-sendouts">Send-outs ({data.sendouts.length})</TabsTrigger>
          <TabsTrigger value="cpt" data-testid="tab-cpt">CPT & billing ({(c.cpt_items || []).length})</TabsTrigger>
          <TabsTrigger value="images" data-testid="tab-images">Images ({(data.attachments || []).length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="narrative" className="mt-4">
          {isMarrowCase(c) && <div className="mb-4"><LabLyncPanel caseId={id} /></div>}
          <div className="grid gap-4 lg:grid-cols-2">
            {[
              ["Gross description", "gross_description", "case-gross-textarea", "gross_description"],
              ["Comments", "comments", "case-comments-textarea", "comments"],
              ["Diagnosis", "diagnosis", "case-diagnosis-textarea", "diagnosis"],
              ["Sign-out notes", "signout_notes", "case-signout-textarea", "signout_notes"],
            ].map(([label, key, tid, dictField]) => {
              const pathologistOnly = ["diagnosis", "signout_notes"].includes(key);
              const locked = !can("cases:write") || c.voided
                || (pathologistOnly && !["pathologist", "admin"].includes(user?.role))
                || (isFinalized && ["diagnosis", "comments", "gross_description", "signout_notes"].includes(key));
              return (
                <NarrativeField key={key} label={label} testid={tid} locked={locked} caseId={id}
                                field={dictField} value={narrative[key]}
                                lockNote={pathologistOnly && !["pathologist", "admin"].includes(user?.role)
                                  ? "Pathologist only" : ""}
                                onChange={(t) => setNarrative((n) => ({ ...n, [key]: t }))} />
              );
            })}
          </div>
          {can("cases:write") && !c.voided && !isFinalized && (
            <Button onClick={saveNarrative} disabled={busy} data-testid="save-narrative-button" className="mt-4 h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
              <Save size={15} className="mr-2" />{busy ? "Saving…" : "Save narrative"}
            </Button>
          )}
          {isFinalized && (
            <p className="mt-4 border border-emerald-800/30 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="report-locked-note">
              This report is {c.report_status.toLowerCase()} and signed by {c.report_signed_by} on {fmtDateTime(c.report_signed_at)}.
              The narrative is locked — file an amended report to change the diagnosis or comment.
            </p>
          )}

          <section className="mt-6 border border-slate-200" data-testid="case-frozen-panel">
            <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Timer size={15} className="text-[#134e4a]" />
              <h3 className="text-base font-semibold text-slate-800 md:text-lg">
                Frozen sections &amp; intraoperative consults ({frozens.length})
              </h3>
              <Link to="/accreditation/frozen" data-testid="case-frozen-link"
                    className="ml-auto text-xs font-semibold text-[#134e4a] hover:underline">
                Open the CAPTraX log →
              </Link>
            </header>
            {frozens.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500">
                None recorded. Log one from CAPTraX → Frozen sections &amp; IOC.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {frozens.map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                      data-testid={`case-frozen-${f.id}`}>
                    <span className="font-semibold text-slate-800">{f.fs_type}</span>
                    {f.specimen_site && <span className="text-slate-600">{f.specimen_site}</span>}
                    <span className={`num font-bold ${f.within_benchmark === false ? "text-red-700"
                      : f.within_benchmark ? "text-[#134e4a]" : "text-amber-800"}`}>
                      {f.minutes !== null ? `${f.minutes} min` : f.clock_running ? `${f.elapsed_minutes} min — running` : "not timed"}
                    </span>
                    <span className="text-xs text-slate-500">
                      benchmark {f.benchmark_minutes} min · {f.surgeon || "surgeon not recorded"}
                      {f.or_room ? ` · OR ${f.or_room}` : ""}
                    </span>
                    {f.fs_diagnosis && <span className="text-slate-700">“{f.fs_diagnosis}”</span>}
                    <span className={`ml-auto px-1.5 py-0.5 text-[11px] font-semibold ${
                      f.correlation === "Discordant" ? "bg-red-800 text-white"
                        : f.correlation === "Concordant" ? "bg-[#134e4a] text-white" : "bg-amber-100 text-amber-900"}`}>
                      {f.correlation}{f.discrepancy === "Major" ? " · major" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mt-6 border border-slate-200" data-testid="case-consult-panel">
            <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Users size={15} className="text-[#134e4a]" />
              <h3 className="text-base font-semibold text-slate-800 md:text-lg">
                Intradepartmental consults ({consults.length})
              </h3>
              <Link to="/accreditation/consults" data-testid="case-consult-link"
                    className="ml-auto text-xs font-semibold text-[#134e4a] hover:underline">
                Open the peer review log →
              </Link>
            </header>
            {consults.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500">
                None recorded. Request a second opinion from CAPTraX → Consults &amp; peer review.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {consults.map((k) => (
                  <li key={k.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                      data-testid={`case-consult-${k.id}`}>
                    <span className="font-semibold text-slate-800">{k.consultant_name}</span>
                    <span className="text-slate-600">{k.reason}</span>
                    {k.mandated && <span className="bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-700">policy review</span>}
                    <span className="text-xs text-slate-500">
                      requested {fmtDateTime(k.requested_at)}
                      {k.hours !== null ? ` · answered in ${k.hours} h` : k.hours_open !== null ? ` · open ${k.hours_open} h` : ""}
                    </span>
                    {k.opinion && <span className="text-slate-700">“{k.opinion.slice(0, 120)}”</span>}
                    <span className={`ml-auto px-1.5 py-0.5 text-[11px] font-semibold ${
                      k.major ? "bg-red-800 text-white"
                        : k.agreement === "Agree" ? "bg-[#134e4a] text-white" : "bg-amber-100 text-amber-900"}`}>
                      {k.agreement}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mt-6 border border-slate-200" data-testid="synoptic-panel">
            <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <ClipboardCheck size={15} className="text-[#134e4a]" />
              <h3 className="text-base font-semibold text-slate-800 md:text-lg">Synoptic report</h3>
              <select value={synTemplateId} disabled={!canRequestRecut || isFinalized || c.voided}
                onChange={(e) => { setSynTemplateId(e.target.value); setSynValues({}); }}
                data-testid="synoptic-template-select"
                className="ml-auto h-11 max-w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:bg-slate-100">
                <option value="">No template</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </header>

            <div className="p-3">
              {!synTemplateId ? (
                <p className="text-sm text-slate-500" data-testid="synoptic-empty">
                  {canRequestRecut
                    ? "Pick a template above to record structured findings that print under the diagnosis."
                    : "No synoptic template applied to this case."}
                </p>
              ) : (
                <>
                  {activeTemplate?.description && (
                    <p className="mb-3 text-xs text-slate-500">{activeTemplate.description}</p>
                  )}
                  <SynopticForm
                    fields={activeTemplate?.fields || []}
                    values={synValues}
                    onChange={setSynValues}
                    disabled={!canRequestRecut || isFinalized || c.voided}
                  />
                  {synMissing.length > 0 && (
                    <p className="mt-3 border-l-2 border-amber-500 bg-amber-50 p-2 text-xs text-amber-900" data-testid="synoptic-missing-warning">
                      Still needed before sign-out: {synMissing.join(", ")}
                    </p>
                  )}
                  {canRequestRecut && !isFinalized && !c.voided && (
                    <Button onClick={saveSynoptic} disabled={synSaving} data-testid="save-synoptic-button"
                      className="mt-3 h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                      <Save size={15} className="mr-2" />{synSaving ? "Saving…" : "Save synoptic report"}
                    </Button>
                  )}
                  {c.synoptic?.updated_by && (
                    <p className="mt-2 text-[11px] text-slate-500" data-testid="synoptic-meta">
                      Last saved by {c.synoptic.updated_by} · {fmtDateTime(c.synoptic.updated_at)}
                    </p>
                  )}
                </>
              )}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="specimens" className="mt-4 space-y-4">
          {can("inventory:write") && !c.voided && (
            <form onSubmit={addSpecimen} className="space-y-3 border border-slate-200 bg-slate-50 p-3" data-testid="add-specimen-form">
              <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Log a</Label>
                  <select value={spec.kind} onChange={(e) => setSpec((s) => ({ ...s, kind: e.target.value }))} data-testid="specimen-kind-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                    <option value="block">Block</option>
                    <option value="slide">Slide</option>
                  </select>
                </div>
                {spec.kind === "block" ? (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Part</Label>
                    <select value={spec.part} onChange={(e) => setSpec((s) => ({ ...s, part: e.target.value }))} data-testid="specimen-part-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      {BLOCK_PARTS.map((p) => <option key={p} value={p}>{`Part ${p}`}</option>)}
                    </select>
                    <p className="mt-1 text-[11px] text-slate-500">Numbers within the part → {spec.part}1, {spec.part}2…</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Cut from block</Label>
                      <select value={spec.parent_block_id} onChange={(e) => setSpec((s) => ({ ...s, parent_block_id: e.target.value }))} data-testid="specimen-parent-block-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                        <option value="">Select block…</option>
                        {blocks.map((b) => <option key={b.id} value={b.id}>{`Block ${b.block_designation || b.label}`}</option>)}
                      </select>
                      {blocks.length === 0 && <p className="mt-1 text-[11px] text-amber-700">Log a block first.</p>}
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Slide type</Label>
                      <select value={spec.slide_type} onChange={(e) => setSpec((s) => ({ ...s, slide_type: e.target.value }))} data-testid="specimen-slide-type-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                        {SLIDE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-600">Starting level</Label>
                      <Input type="number" min="1" max="99" value={spec.level} onChange={(e) => setSpec((s) => ({ ...s, level: e.target.value }))}
                        placeholder="auto" data-testid="specimen-level-input" className="mt-1 h-11 border-slate-300 bg-white" />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">
                    {spec.kind === "block" ? "Number of blocks" : "Number of levels"}
                  </Label>
                  <Input type="number" min="1" max="20" value={spec.quantity} onChange={(e) => setSpec((s) => ({ ...s, quantity: e.target.value }))} data-testid="specimen-quantity-input" className="mt-1 h-11 border-slate-300 bg-white" />
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs text-slate-600" data-testid="specimen-preview-barcode">
                  Will label: {previewBarcode}
                </span>
                <Button type="submit" data-testid="add-specimen-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Plus size={15} className="mr-1" /> Log {spec.kind === "block" ? "block(s)" : "slide(s)"}
                </Button>
              </div>
            </form>
          )}

          <div className="border border-slate-200" data-testid="case-recuts-panel">
            <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Scissors size={15} className="text-[#134e4a]" />
              <h3 className="text-base font-semibold text-slate-800 md:text-lg">Histology requests ({requests.length})</h3>
              {canRequestRecut && !c.voided && (
                <Button size="sm" onClick={() => setRecutOpen(true)} data-testid="request-recut-button" className="ml-auto h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Plus size={14} className="mr-1" /> New request
                </Button>
              )}
            </header>
            {requests.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500">No requests on this case.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {requests.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-testid={`case-recut-${r.id}`}>
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${RECUT_BADGE[r.status] || "bg-slate-200"}`}>{r.status}</span>
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      {r.kind === "sendout" ? "Send-out" : "In-house"}
                    </span>
                    <span className="text-slate-700">{r.request}</span>
                    <span className="font-mono text-xs text-slate-700">{r.target}</span>
                    {r.kind === "sendout" && <span className="text-xs text-slate-600">→ {r.destination}</span>}
                    {r.priority === "STAT" && <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-bold text-white">STAT</span>}
                    <span className="text-xs text-slate-500">by {r.requested_by} · {fmtDateTime(r.requested_at)}</span>
                    {r.note && <span className="text-xs text-slate-600">“{r.note}”</span>}
                    {r.outcome && <span className="font-mono text-xs text-green-800">{r.outcome}</span>}
                    {can("inventory:write") && r.kind === "inhouse" && !["Completed", "Cancelled"].includes(r.status) && (
                      <span className="ml-auto flex items-center gap-1.5">
                        {r.status === "Pending" && (
                          <Button size="sm" variant="outline" onClick={() => setRecutStatus(r, "In progress")} data-testid={`case-recut-accept-${r.id}`} className="h-9">Accept</Button>
                        )}
                        <Button size="sm" onClick={() => setRecutStatus(r, "Completed")} data-testid={`case-recut-complete-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">Complete</Button>
                        <Button size="sm" variant="outline" onClick={() => setRecutStatus(r, "Cancelled")} data-testid={`case-recut-cancel-${r.id}`} className="h-9">Cancel</Button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <RequestDialog open={recutOpen} onOpenChange={setRecutOpen} caseId={id} onDone={load}
                         canOrderInhouse={canRequestRecut} canOrderSendout={can("sendouts:write")} />
          <div className="flex justify-end">
            <Button onClick={() => setPrintItems(data.specimens)} disabled={!data.specimens.length}
                    data-testid="print-case-labels-printer-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
              <Printer size={15} className="mr-2" /> Label printer
            </Button>
            <Button variant="outline" onClick={() => setPrintItems([])}
                    data-testid="print-container-label-button" className="h-11">
              <Printer size={15} className="mr-2" /> Container label
            </Button>
            <Button variant="outline" onClick={() => setLabels(data.specimens)} disabled={!data.specimens.length} data-testid="print-case-labels-button" className="h-11">
              <Printer size={15} className="mr-2" /> Quick sheet
            </Button>
          </div>
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full min-w-[760px] text-sm" data-testid="case-specimens-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">Barcode</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Block / Level</th>
                  <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.specimens.map((s) => (
                  <Fragment key={s.id}>
                  <tr className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`specimen-row-${s.barcode}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{s.barcode}</td>
                    <td className="px-3 py-2">{s.kind === "slide" ? (s.slide_type || s.stain || "Slide") : "Block"}</td>
                    <td className="px-3 py-2 num text-slate-700">
                      {s.kind === "slide"
                        ? `Block ${s.block_designation || "—"} · L${s.level || "—"}`
                        : `Block ${s.block_designation || s.label}`}
                    </td>
                    <td className="px-3 py-2">
                      {s.checked_out
                        ? <span className="bg-amber-700 px-1.5 py-0.5 text-[11px] font-semibold text-white">Out · {s.checked_out_to}</span>
                        : <span className="text-green-800">In inventory</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setHistoryFor(historyFor === s.id ? null : s.id)}
                          data-testid={`specimen-history-toggle-${s.barcode}`} className="h-9">
                          <History size={14} className="mr-1" /> Trail
                        </Button>
                        {can("inventory:write") && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => toggleCheckout(s)} data-testid={`toggle-checkout-${s.barcode}`} className="h-9">
                              {s.checked_out ? "Return" : "Check out"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditSpec(s)}
                                    data-testid={`correct-specimen-${s.barcode}`} className="h-9">
                              <PencilLine size={14} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setVoidSpec(s)}
                                    data-testid={`void-specimen-${s.barcode}`} className="h-9 border-red-300 text-red-800 hover:bg-red-50">
                              <Ban size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {historyFor === s.id && (
                    <tr className="border-b border-slate-100 bg-slate-50" data-testid={`specimen-history-${s.barcode}`}>
                      <td colSpan={5} className="px-3 py-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Handling trail — {s.barcode}</div>
                        <ul className="mt-1 space-y-0.5">
                          {(s.history || []).length === 0 && <li className="text-sm text-slate-500">No handling recorded.</li>}
                          {(s.history || []).map((h, idx) => (
                            <li key={`${s.id}-${idx}`} className="font-mono text-xs text-slate-700">
                              • {fmtDateTime(h.at)} — {h.action}
                              {h.to ? ` to ${h.to}` : ""} by {h.by}{h.note ? ` · ${h.note}` : ""}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {data.specimens.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">No blocks or slides logged yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="sendouts" className="mt-4 space-y-4">
          {can("sendouts:write") && !c.voided && (
            <div className="border border-slate-200 bg-slate-50 p-3">
              {!showSendout ? (
                <Button onClick={() => setShowSendout(true)} data-testid="flag-sent-out-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Send size={15} className="mr-2" /> Flag this case as sent out
                </Button>
              ) : (
                <form onSubmit={createSendout} className="grid gap-3 md:grid-cols-2" data-testid="case-sendout-form">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Facility name *</Label>
                    <Input required value={sendout.destination_lab} onChange={(e) => setSendout((s) => ({ ...s, destination_lab: e.target.value }))}
                      data-testid="case-sendout-facility-input" className="mt-1 h-11 border-slate-300 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Facility address</Label>
                    <Input value={sendout.destination_address} onChange={(e) => setSendout((s) => ({ ...s, destination_address: e.target.value }))}
                      data-testid="case-sendout-address-input" className="mt-1 h-11 border-slate-300 bg-white" placeholder="Street, city, state, ZIP" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Test requested *</Label>
                    <Input required value={sendout.test_requested} onChange={(e) => setSendout((s) => ({ ...s, test_requested: e.target.value }))}
                      data-testid="case-sendout-test-input" className="mt-1 h-11 border-slate-300 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Reason for send-out</Label>
                    <Input value={sendout.reason} onChange={(e) => setSendout((s) => ({ ...s, reason: e.target.value }))}
                      data-testid="case-sendout-reason-input" className="mt-1 h-11 border-slate-300 bg-white" placeholder="e.g. Expert consult" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Current status</Label>
                    <select value={sendout.sendout_status} onChange={(e) => setSendout((s) => ({ ...s, sendout_status: e.target.value }))}
                      data-testid="case-sendout-status-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                      {SENDOUT_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Materials sent</Label>
                    <Input value={sendout.materials_sent} onChange={(e) => setSendout((s) => ({ ...s, materials_sent: e.target.value }))}
                      data-testid="case-sendout-materials-input" className="mt-1 h-11 border-slate-300 bg-white" placeholder="e.g. 2 H&E slides, block B1" />
                  </div>
                  <div className="md:col-span-2 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowSendout(false)} data-testid="case-sendout-cancel" className="h-11">Cancel</Button>
                    <Button type="submit" data-testid="case-sendout-submit" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">Record send-out</Button>
                  </div>
                </form>
              )}
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full min-w-[1100px] text-sm" data-testid="case-sendouts-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">Facility</th><th className="px-3 py-2">Address</th><th className="px-3 py-2">Test</th>
                  <th className="px-3 py-2">Reason</th><th className="px-3 py-2">Date sent</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">CPT</th><th className="px-3 py-2">Billing</th><th className="px-3 py-2">Results</th><th className="px-3 py-2">Materials</th>
                </tr>
              </thead>
              <tbody>
                {data.sendouts.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100" data-testid={`case-sendout-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.destination_lab}</td>
                    <td className="px-3 py-2 text-slate-600">{s.destination_address || "—"}</td>
                    <td className="px-3 py-2">{s.test_requested}</td>
                    <td className="px-3 py-2 text-slate-600">{s.reason || "—"}</td>
                    <td className="px-3 py-2 num text-slate-600">{fmtDateTime(s.date_sent)}</td>
                    <td className="px-3 py-2">
                      {can("sendouts:write") ? (
                        <select value={s.sendout_status || "Pending pickup"} onChange={(e) => updateSendoutStatus(s, e.target.value)}
                          data-testid={`case-sendout-status-${s.id}`} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-xs">
                          {SENDOUT_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      ) : (s.sendout_status || "Pending pickup")}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700">{s.cpt_codes || "—"}</td>
                    <td className="px-3 py-2">{s.billing_status} · {money(s.charge_amount)}</td>
                    <td className="px-3 py-2">{s.results_received ? `Received ${fmtDateTime(s.results_date)}` : "Pending"}</td>
                    <td className="px-3 py-2">{s.materials_returned ? "Returned" : "Outstanding"}</td>
                  </tr>
                ))}
                {data.sendouts.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-slate-500">
                    This case has not been sent out.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="cpt" className="mt-4">
          <section className="border border-slate-200" data-testid="cpt-panel">
            <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Receipt size={15} className="text-[#134e4a]" />
              <h3 className="text-base font-semibold text-slate-800 md:text-lg">CPT codes used on this case</h3>
              {c.cpt_updated_by && (
                <span className="ml-auto text-[11px] text-slate-500" data-testid="cpt-meta">
                  Last coded by {c.cpt_updated_by} · {fmtDateTime(c.cpt_updated_at)}
                </span>
              )}
            </header>

            <div className="space-y-4 p-3">
              {canCode && !c.voided && (
                <form onSubmit={addCpt} className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3" data-testid="cpt-add-form">
                  <div className="min-w-[240px] flex-1">
                    <Label className="text-xs uppercase tracking-wide text-slate-600">CPT code</Label>
                    <Input list="case-cpt-codes" value={cptDraft.code}
                      onChange={(e) => setCptDraft((d) => ({ ...d, code: e.target.value }))}
                      data-testid="cpt-code-input" placeholder="e.g. 88305" className="mt-1 h-11 border-slate-300" />
                    <datalist id="case-cpt-codes">
                      {cptSuggestions.map((k) => <option key={k} value={k.split(" — ")[0]} label={k} />)}
                    </datalist>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Units</Label>
                    <Input type="number" min="1" max="99" value={cptDraft.units}
                      onChange={(e) => setCptDraft((d) => ({ ...d, units: e.target.value }))}
                      data-testid="cpt-units-input" className="mt-1 h-11 border-slate-300" />
                  </div>
                  <Button type="submit" data-testid="cpt-add-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                    <Plus size={15} className="mr-2" /> Add code
                  </Button>
                </form>
              )}

              {cpt.items.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="cpt-empty">No CPT codes recorded on this case yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm" data-testid="cpt-table">
                    <thead className="bg-white text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-2">CPT</th><th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2 text-right">Units</th><th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {cpt.items.map((it) => (
                        <tr key={it.code} className="border-b border-slate-100" data-testid={`cpt-row-${it.code}`}>
                          <td className="px-3 py-2 font-mono font-semibold text-slate-900">{it.code}</td>
                          <td className="px-3 py-2 text-slate-700">{it.description || "—"}</td>
                          <td className="num px-3 py-2 text-right text-slate-800">{it.units}</td>
                          <td className="px-3 py-2 text-right">
                            {canCode && !c.voided && (
                              <button onClick={() => setCpt((p) => ({ ...p, items: p.items.filter((x) => x.code !== it.code) }))}
                                data-testid={`cpt-remove-${it.code}`} className="text-xs font-semibold text-red-800 hover:underline">
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Billing note</Label>
                <Textarea value={cpt.note} onChange={(e) => setCpt((p) => ({ ...p, note: e.target.value }))}
                  disabled={!canCode || c.voided} data-testid="cpt-note-textarea"
                  className="mt-1 min-h-[90px] border-slate-300 focus-visible:ring-2 focus-visible:ring-[#134e4a]"
                  placeholder="Optional — modifiers, payer notes, why a level was chosen." />
              </div>

              {canCode && !c.voided && (
                <Button onClick={saveCpt} disabled={cptSaving} data-testid="save-cpt-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Save size={15} className="mr-2" />{cptSaving ? "Saving…" : "Save billing codes"}
                </Button>
              )}
              <p className="text-xs text-slate-500">
                Codes print in a “Billing codes” block on the final report and stay editable after sign-out.
              </p>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="images" className="mt-4 space-y-4">
          <ArchivedPaperPanel caseId={id} accession={c.accession_number} />
          {annotating && (
            <ImageAnnotator
              src={annotating.src}
              title={annotating.att.caption || annotating.att.original_filename}
              initialShapes={annotating.shapes}
              onSave={saveAnnotations}
              onClose={() => setAnnotating(null)}
            />
          )}

          {can("cases:write") && !c.voided && (
            <div className="border border-slate-200 bg-slate-50 p-3" data-testid="camera-capture-panel">
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 bg-[#134e4a] px-4 text-sm font-semibold text-white hover:bg-[#0f3f3c]" data-testid="camera-capture-label">
                  <Camera size={16} /> Take photo
                  <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                    onChange={(e) => { addShots(e.target.files); e.target.value = ""; }} data-testid="camera-capture-input" />
                </label>
                <span className="text-xs text-slate-600">Opens the phone camera. Snap as many shots as you need, then attach them all at once.</span>
              </div>

              {shots.length > 0 && (
                <div className="mt-3 space-y-3" data-testid="camera-shot-queue">
                  {shots.map((s, i) => (
                    <div key={s.url} className="flex flex-wrap items-center gap-3 border border-slate-200 bg-white p-2" data-testid={`camera-shot-${i}`}>
                      <img src={s.url} alt={`Shot ${i + 1}`} className="h-16 w-16 object-cover" />
                      <select value={s.kind} onChange={(e) => setShots((p) => p.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))}
                        data-testid={`camera-shot-kind-${i}`} className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
                        {["Gross photo", "Slide scan", "Requisition", "Other"].map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                      <Input value={s.caption} onChange={(e) => setShots((p) => p.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))}
                        placeholder="Caption (optional)" data-testid={`camera-shot-caption-${i}`} className="h-11 max-w-xs border-slate-300" />
                      <button onClick={() => setShots((p) => p.filter((_, j) => j !== i))} data-testid={`camera-shot-remove-${i}`}
                        className="ml-auto inline-flex h-10 w-10 items-center justify-center text-slate-500 hover:text-red-800" aria-label="Remove shot">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <Button onClick={uploadShots} disabled={uploading} data-testid="camera-upload-all-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                    <ImagePlus size={15} className="mr-2" />{uploading ? "Attaching…" : `Attach ${shots.length} photo(s)`}
                  </Button>
                </div>
              )}
            </div>
          )}

          {can("cases:write") && !c.voided && (
            <form onSubmit={uploadImage} className="grid gap-3 border border-slate-200 bg-slate-50 p-3 md:grid-cols-4" data-testid="attachment-upload-form">
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Image file</Label>
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/tiff,application/pdf"
                  onChange={(e) => setUpload((u) => ({ ...u, file: e.target.files?.[0] || null }))}
                  data-testid="attachment-file-input" className="mt-1 block w-full text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Type</Label>
                <select value={upload.kind} onChange={(e) => setUpload((u) => ({ ...u, kind: e.target.value }))}
                  data-testid="attachment-kind-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {["Gross photo", "Slide scan", "Requisition", "Other"].map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Caption</Label>
                <Input value={upload.caption} onChange={(e) => setUpload((u) => ({ ...u, caption: e.target.value }))}
                  data-testid="attachment-caption-input" className="mt-1 h-11 border-slate-300 bg-white" />
              </div>
              <div className="md:col-span-4">
                <Button type="submit" disabled={uploading} data-testid="attachment-upload-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <ImagePlus size={15} className="mr-2" />{uploading ? "Uploading…" : "Attach image"}
                </Button>
                <span className="ml-3 text-xs text-slate-500">JPEG, PNG, GIF, WEBP, TIFF or PDF · up to 25 MB</span>
              </div>
            </form>
          )}
          {(data.attachments || []).length === 0 ? (
            <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="attachments-empty">
              No images attached to this case yet.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="attachments-grid">
              {(data.attachments || []).map((a) => (
                <div key={a.id} className="border border-slate-200" data-testid={`attachment-card-${a.id}`}>
                  <div className="flex h-52 items-center justify-center bg-slate-100">
                    {previews[a.id] ? (
                      <img src={previews[a.id]} alt={a.caption || a.original_filename} className="h-52 w-full object-contain" data-testid={`attachment-image-${a.id}`} />
                    ) : (
                      <Button variant="outline" onClick={() => loadPreview(a)} data-testid={`attachment-load-${a.id}`} className="h-11">
                        <Eye size={15} className="mr-2" /> View image
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="bg-[#134e4a]/10 px-1.5 py-0.5 text-xs font-semibold text-[#134e4a]">{a.kind}</span>
                      <span className="num text-xs text-slate-500">{Math.round((a.size || 0) / 1024)} KB</span>
                    </div>
                    <div className="truncate text-sm font-medium text-slate-800">{a.caption || a.original_filename}</div>
                    <div className="text-xs text-slate-500">{a.uploaded_by} · {fmtDateTime(a.created_at)}</div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {a.has_annotations && (
                        <span className="bg-amber-600 px-1.5 py-0.5 text-[11px] font-semibold text-white" data-testid={`attachment-annotated-badge-${a.id}`}>Annotated</span>
                      )}
                      {previews[a.id] && (
                        <a href={previews[a.id]} target="_blank" rel="noreferrer"
                          className="text-xs font-semibold text-[#134e4a] hover:underline" data-testid={`attachment-open-${a.id}`}>
                          Open full size
                        </a>
                      )}
                      {["pathologist", "admin", "lab_director"].includes(user?.role) && !c.voided && (a.content_type || "").startsWith("image/") && (
                        <button onClick={() => openAnnotator(a)} data-testid={`attachment-annotate-${a.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#134e4a] hover:underline">
                          <PenLine size={12} /> {a.has_annotations ? "Edit marks" : "Annotate"}
                        </button>
                      )}
                      {["pathologist", "admin", "lab_director"].includes(user?.role) && !c.voided && (a.content_type || "").startsWith("image/") && (
                        <button onClick={() => toggleReportImage(a)} data-testid={`attachment-report-toggle-${a.id}`}
                          className={`inline-flex items-center gap-1 text-xs font-semibold ${a.include_in_report ? "text-[#134e4a]" : "text-slate-500"} hover:underline`}>
                          <FileText size={12} /> {a.include_in_report ? "On report" : "Add to report"}
                        </button>
                      )}
                      {can("signout") && (
                        <button onClick={() => removeImage(a)} data-testid={`attachment-delete-${a.id}`}
                          className="text-xs font-semibold text-red-800 hover:underline">Remove</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <ol className="border-l-2 border-slate-200 pl-5" data-testid="case-history-timeline">
            {data.events.map((e) => (
              <li key={e.id} className="relative pb-5" data-testid={`history-event-${e.id}`}>
                <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-[#134e4a]" />
                <div className="text-sm font-semibold text-slate-900">{e.status}</div>
                <div className="num text-xs text-slate-500">{fmtDateTime(e.at)} · {e.user_name}</div>
                {e.note && <div className="mt-1 text-sm text-slate-700">{e.note}</div>}
              </li>
            ))}
          </ol>
        </TabsContent>
      </Tabs>
    </div>
  );
}
